// src/utils/paymentStorage.js
import { getApiBase } from './getApiBase';

const LS_PAYMENT_RATES = 'planner:paymentRates';
const LS_EVENT_STAFF_ROLES = 'planner:eventStaffRoles';
const LS_EVENT_AMOUNTS = 'planner:eventAmounts';
const LS_DEFAULT_FUNCTION_RATES = 'planner:defaultFunctionRates';
const LS_TAX_RATE = 'planner:taxRate';

/**
 * Zapisuje dane płatności do bazy danych (w tle, nie blokuje)
 */
// Throttling dla zapisów do bazy (aby uniknąć zbyt częstych zapisów)
let lastSaveTime = 0;
const SAVE_THROTTLE_MS = 2000; // Zapisz maksymalnie co 2 sekundy

async function savePaymentDataToDB(data) {
    try {
        // W środowisku testowym, jeśli fetch nie jest dostępny, pomiń zapis do bazy
        if (typeof fetch === 'undefined' || !fetch) {
            return;
        }

        let clientId = '';
        try {
            clientId = sessionStorage.getItem('planner:instanceId') || '';
        } catch {
            clientId = '';
        }
        const headers = {
            'Content-Type': 'application/json',
            ...(clientId ? { 'x-client-id': clientId } : {}),
        };
        
        // Throttling - zapisz tylko jeśli minęło wystarczająco czasu od ostatniego zapisu
        const now = Date.now();
        if (now - lastSaveTime < SAVE_THROTTLE_MS) {
            // Zaplanuj zapis na później (debounce)
            if (!savePaymentDataToDB.pendingTimeout) {
                savePaymentDataToDB.pendingTimeout = setTimeout(() => {
                    savePaymentDataToDB.pendingTimeout = null;
                    lastSaveTime = Date.now();
                    fetch(`${getApiBase()}/api/saveData`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(data),
                    }).catch(() => {}); // Ignoruj błędy
                }, SAVE_THROTTLE_MS - (now - lastSaveTime));
            }
            return;
        }
        
        lastSaveTime = now;
        const response = await fetch(`${getApiBase()}/api/saveData`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        
        if (!response || !response.ok) {
            const errorText = response ? await response.text() : 'No response';
            console.error(`❌ Błąd zapisu danych płatności do bazy (${response?.status || 'no status'}):`, errorText);
            return;
        }
        
        await response.json();
    } catch (err) {
        console.error('❌ Błąd zapisu danych płatności do bazy:', err);
        // Nie rzucamy błędu - localStorage działa jako backup
    }
}

/**
 * Odczytuje domyślne kwoty dla pracowników
 */
export function loadPaymentRates() {
    try {
        const data = localStorage.getItem(LS_PAYMENT_RATES);
        return data ? JSON.parse(data) : {};
    } catch {
        return {};
    }
}

/**
 * Zapisuje domyślne kwoty dla pracowników
 */
export function savePaymentRates(rates) {
    try {
        localStorage.setItem(LS_PAYMENT_RATES, JSON.stringify(rates));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ paymentRates: rates });
        return true;
    } catch {
        return false;
    }
}

/**
 * Odczytuje przypisane stanowiska dla pracowników w wydarzeniach
 */
export function loadEventStaffRoles() {
    try {
        const data = localStorage.getItem(LS_EVENT_STAFF_ROLES);
        return data ? JSON.parse(data) : {};
    } catch {
        return {};
    }
}

/**
 * Zapisuje przypisane stanowiska dla pracowników w wydarzeniach
 */
export function saveEventStaffRoles(roles) {
    try {
        localStorage.setItem(LS_EVENT_STAFF_ROLES, JSON.stringify(roles));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ eventStaffRoles: roles });
        return true;
    } catch {
        return false;
    }
}

/**
 * Aktualizuje stanowisko dla pracownika w wydarzeniu
 */
export function updateEventStaffRole(date, eventName, employeeName, position) {
    const roles = loadEventStaffRoles();
    
    if (!roles[date]) {
        roles[date] = {};
    }
    if (!roles[date][eventName]) {
        roles[date][eventName] = {};
    }
    
    if (position) {
        roles[date][eventName][employeeName] = position;
    } else {
        delete roles[date][eventName][employeeName];
        // Usuń puste obiekty
        if (Object.keys(roles[date][eventName]).length === 0) {
            delete roles[date][eventName];
        }
        if (Object.keys(roles[date]).length === 0) {
            delete roles[date];
        }
    }
    
    saveEventStaffRoles(roles);
    return roles;
}

/**
 * Odczytuje domyślne kwoty za funkcje (oferowane klientom)
 */
export function loadDefaultFunctionRates() {
    try {
        const data = localStorage.getItem(LS_DEFAULT_FUNCTION_RATES);
        return data ? JSON.parse(data) : {};
    } catch {
        return {};
    }
}

/**
 * Zapisuje domyślne kwoty za funkcje (oferowane klientom)
 */
export function saveDefaultFunctionRates(rates) {
    try {
        localStorage.setItem(LS_DEFAULT_FUNCTION_RATES, JSON.stringify(rates));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ defaultFunctionRates: rates });
        return true;
    } catch {
        return false;
    }
}

/**
 * Odczytuje procent podatku
 */
export function loadTaxRate() {
    try {
        const data = localStorage.getItem(LS_TAX_RATE);
        return data ? parseFloat(data) : 0;
    } catch {
        return 0;
    }
}

/**
 * Zapisuje procent podatku
 */
export function saveTaxRate(rate) {
    try {
        const numRate = parseFloat(rate) || 0;
        localStorage.setItem(LS_TAX_RATE, String(numRate));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ taxRate: numRate });
        return true;
    } catch {
        return false;
    }
}

/**
 * Odczytuje wynagrodzenia pracowników za wydarzenia
 */
export function loadEventStaffPayments() {
    try {
        const data = localStorage.getItem('planner:eventStaffPayments');
        return data ? JSON.parse(data) : {};
    } catch {
        return {};
    }
}

/**
 * Zapisuje wynagrodzenia pracowników za wydarzenia
 */
export function saveEventStaffPayments(payments) {
    try {
        localStorage.setItem('planner:eventStaffPayments', JSON.stringify(payments));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ eventStaffPayments: payments });
        return true;
    } catch {
        return false;
    }
}

/**
 * Aktualizuje wynagrodzenie pracownika za wydarzenie
 */
export function updateEventStaffPayment(date, eventName, employeeName, baseAmount, additionalCosts) {
    const payments = loadEventStaffPayments();
    
    if (!payments[date]) {
        payments[date] = {};
    }
    if (!payments[date][eventName]) {
        payments[date][eventName] = {};
    }
    
    const numBase = parseFloat(baseAmount) || 0;
    const numAdditional = parseFloat(additionalCosts) || 0;
    
    if (numBase > 0 || numAdditional > 0) {
        payments[date][eventName][employeeName] = {
            baseAmount: numBase,
            additionalCosts: numAdditional,
        };
    } else {
        delete payments[date][eventName][employeeName];
        if (Object.keys(payments[date][eventName]).length === 0) {
            delete payments[date][eventName];
        }
        if (Object.keys(payments[date]).length === 0) {
            delete payments[date];
        }
    }
    
    saveEventStaffPayments(payments);
    return payments;
}

/**
 * Odczytuje kwoty za wydarzenia
 */
export function loadEventAmounts() {
    try {
        const data = localStorage.getItem(LS_EVENT_AMOUNTS);
        return data ? JSON.parse(data) : {};
    } catch {
        return {};
    }
}

/**
 * Zapisuje kwoty za wydarzenia
 */
export function saveEventAmounts(amounts) {
    try {
        localStorage.setItem(LS_EVENT_AMOUNTS, JSON.stringify(amounts));
        // Zapisz również do bazy danych
        savePaymentDataToDB({ eventAmounts: amounts });
        return true;
    } catch {
        return false;
    }
}

/**
 * Aktualizuje kwotę za wydarzenie
 */
export function updateEventAmount(date, eventName, amount) {
    const amounts = loadEventAmounts();
    
    if (!amounts[date]) {
        amounts[date] = {};
    }
    
    const numAmount = parseFloat(amount) || 0;
    if (numAmount > 0) {
        amounts[date][eventName] = numAmount;
    } else {
        delete amounts[date][eventName];
        if (Object.keys(amounts[date]).length === 0) {
            delete amounts[date];
        }
    }
    
    saveEventAmounts(amounts);
    return amounts;
}

/**
 * Automatycznie przypisuje stanowiska na podstawie kolejności w liście staff
 * Pierwsza osoba = stage_manager, reszta = inspicjent
 * @param {Object} eventsByDate - wydarzenia pogrupowane po datach
 * @param {Object} existingRoles - opcjonalnie istniejące role (jeśli są przekazane, użyj ich zamiast ładować z localStorage)
 */
const cloneDeep = (obj) => {
    try {
        return JSON.parse(JSON.stringify(obj || {}));
    } catch {
        return {};
    }
};

export function autoAssignRolesFromStaff(eventsByDate, existingRoles = null) {
    // Jeśli przekazano istniejące role, użyj ich (kopii). W przeciwnym razie załaduj z localStorage
    const roles = existingRoles !== null ? cloneDeep(existingRoles) : loadEventStaffRoles();
    let changed = false;
    const seenDates = new Set();
    
    Object.entries(eventsByDate || {}).forEach(([date, events]) => {
        seenDates.add(date);
        events.forEach((event) => {
            if (!event.event || event._isFreeDay) return;
            
            const staff = event.staff || [];
            if (staff.length === 0) return;
            
            if (!roles[date]) {
                roles[date] = {};
            }
            if (!roles[date][event.event]) {
                roles[date][event.event] = {};
            }
            
            // Przypisz stanowiska: pierwsza = stage_manager, reszta = inspicjent
            const staffSet = new Set();
            staff.forEach((s, idx) => {
                const empName = typeof s === 'string' ? s : s?.name;
                if (!empName) return;
                staffSet.add(empName);
                
                const currentRole = roles[date][event.event][empName];
                const expectedRole = idx === 0 ? 'stage_manager' : 'inspicjent';
                
                // Przypisz tylko jeśli nie ma jeszcze przypisanego stanowiska
                if (!currentRole) {
                    roles[date][event.event][empName] = expectedRole;
                    changed = true;
                }
            });

            // Usuń role osób, które już nie są w staff
            Object.keys(roles[date][event.event]).forEach((empName) => {
                if (!staffSet.has(empName)) {
                    delete roles[date][event.event][empName];
                    changed = true;
                }
            });

            if (Object.keys(roles[date][event.event]).length === 0) {
                delete roles[date][event.event];
                changed = true;
            }
        });

        // Usuń wydarzenia, których nie było w aktualnym harmonogramie
        if (roles[date]) {
            const eventNames = new Set((events || []).map((e) => e.event).filter(Boolean));
            Object.keys(roles[date]).forEach((eventName) => {
                if (!eventNames.has(eventName)) {
                    delete roles[date][eventName];
                    changed = true;
                }
            });
            if (Object.keys(roles[date]).length === 0) {
                delete roles[date];
                changed = true;
            }
        }
    });

    // Usuń daty, które nie istnieją już w harmonogramie
    Object.keys(roles).forEach((date) => {
        if (!seenDates.has(date)) {
            delete roles[date];
            changed = true;
        }
    });
    
    if (changed) {
        saveEventStaffRoles(roles);
    }
    
    return roles;
}

